/**
 * Motion Presets — apply a full choreography to the current canvas with one click.
 * Each preset is a function: (fabricObjects[]) → animations[]
 * The heuristic decides which type fits each object based on its Fabric shape.
 */

const isHeadline = (o) => o?.type === "textbox" && (o.fontSize ?? 0) > 40;
const isTextbox  = (o) => o?.type === "textbox" || o?.type === "i-text";
const isRoundedRect = (o) => o?.type === "rect" && (o.rx > 8 || o.ry > 8);
const isCircle = (o) => o?.type === "circle";
const isImage  = (o) => o?.type === "image";

/** Helper to build animation entries quickly. */
const anim = (i, type, start_at, duration) => ({ object_index: i, type, start_at, duration });

/** ---------- 10 PRESETS ---------- */
export const MOTION_PRESETS = [
  {
    id: "cinematic_hero",
    label: "Cinematic Hero",
    desc: "Slow, dramatic reveals. Best for luxury & brand.",
    duration_ms: 6500,
    gradient: "linear-gradient(135deg, #0B0B22, #4c1d95)",
    build: (objs) => objs.map((o, i) => {
      if (isHeadline(o)) return anim(i, "typewriter",   400 + i * 200, 1500);
      if (isRoundedRect(o)) return anim(i, "zoom-in",   2800 + i * 200, 900);
      if (isCircle(o))   return anim(i, "fade-in",       i * 250,       1400);
      return anim(i, "fade-in", 200 + i * 220, 1200);
    }),
  },
  {
    id: "fast_sale_bash",
    label: "Fast Sale Bash",
    desc: "High-energy cuts. Flash-sale fuel.",
    duration_ms: 3000,
    gradient: "linear-gradient(135deg, #7f1d1d, #f59e0b)",
    build: (objs) => objs.map((o, i) => {
      if (isRoundedRect(o)) return anim(i, "bounce",       300 + i * 90, 500);
      if (isHeadline(o))   return anim(i, "slide-in-right", 150 + i * 90, 420);
      if (isCircle(o))     return anim(i, "zoom-in",        i * 80,       350);
      return anim(i, "slide-in-down", i * 90, 380);
    }),
  },
  {
    id: "elegant_luxury",
    label: "Elegant Luxury",
    desc: "Quiet confidence. Fashion, beauty, fine dining.",
    duration_ms: 6000,
    gradient: "linear-gradient(135deg, #0c1117, #d4af37)",
    build: (objs) => objs.map((o, i) => {
      if (isHeadline(o))    return anim(i, "typewriter", 300 + i * 350, 1800);
      if (isRoundedRect(o)) return anim(i, "fade-in",   3500 + i * 150, 1000);
      return anim(i, "fade-in", i * 300, 1200);
    }),
  },
  {
    id: "social_reel_burst",
    label: "Social Reel Burst",
    desc: "Instagram Reel default. Punchy + friendly.",
    duration_ms: 4500,
    gradient: "linear-gradient(135deg, #ff4d9d, #7c3aed)",
    build: (objs) => objs.map((o, i) => {
      if (isRoundedRect(o)) return anim(i, "bounce",        2400 + i * 100, 700);
      if (isHeadline(o))    return anim(i, "slide-in-up",    800 + i * 300, 650);
      if (isCircle(o))      return anim(i, "fade-in",         i * 250,        700);
      if (isImage(o))       return anim(i, "fade-in",         i * 220,        700);
      return anim(i, "slide-in-up", 400 + i * 240, 600);
    }),
  },
  {
    id: "typewriter_story",
    label: "Typewriter Story",
    desc: "Every word types itself. Docs, news, storytelling.",
    duration_ms: 6500,
    gradient: "linear-gradient(135deg, #0f0f1c, #eab308)",
    build: (objs) => {
      // Strictly sequential: each textbox waits for the previous to finish typing
      let cursor = 0;
      return objs.map((o, i) => {
        if (isTextbox(o)) {
          const dur = 300 + Math.min(1400, (o.text?.length || 0) * 45);
          const a = anim(i, "typewriter", cursor, dur);
          cursor += dur - 100;
          return a;
        }
        return anim(i, "fade-in", i * 220, 600);
      });
    },
  },
  {
    id: "news_ticker",
    label: "News Ticker",
    desc: "Everything slides in from the right, cable-news style.",
    duration_ms: 4000,
    gradient: "linear-gradient(135deg, #052e2b, #06b6d4)",
    build: (objs) => objs.map((o, i) => anim(i, "slide-in-right", i * 240, 520)),
  },
  {
    id: "pop_attention",
    label: "Pop Attention",
    desc: "Bounce everything. Demands a look.",
    duration_ms: 3500,
    gradient: "linear-gradient(135deg, #111827, #ef4444)",
    build: (objs) => objs.map((o, i) => anim(i, "bounce", i * 180, 650)),
  },
  {
    id: "gentle_reveal",
    label: "Gentle Reveal",
    desc: "Calm cascading fade-ins. Corporate & healthcare.",
    duration_ms: 4500,
    gradient: "linear-gradient(135deg, #052e2b, #34d399)",
    build: (objs) => objs.map((o, i) => anim(i, "fade-in", i * 280, 900)),
  },
  {
    id: "top_down_drop",
    label: "Top-Down Drop",
    desc: "Objects rain in from above, top of stack first.",
    duration_ms: 3800,
    gradient: "linear-gradient(135deg, #0a1220, #60a5fa)",
    build: (objs) => objs.map((o, i) => anim(i, "slide-in-down", i * 180, 520)),
  },
  {
    id: "focus_pull",
    label: "Focus Pull",
    desc: "Everything zooms into focus, one by one.",
    duration_ms: 4200,
    gradient: "linear-gradient(135deg, #1f0a1e, #ec4899)",
    build: (objs) => objs.map((o, i) => anim(i, "zoom-in", i * 220, 700)),
  },
];
